import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import cn from 'classnames';

import Edit from  'shared/ui/icons/General/Edit';
import Checkbox from 'shared/ui/Checkbox/Checkbox';
import Correct from 'shared/ui/icons/General/Correct';
import ArrowDown from 'shared/ui/icons/General/ArrowDown';
import Close from 'shared/ui/icons/General/Close';
import Wrong from 'shared/ui/icons/General/Wrong';
import { isTranslate } from 'shared/lib/lang';
import { getInputState } from 'shared/ui/Input/inputHelper';

import {t} from '@lingui/core/macro';

import {PALETTE} from 'shared/config/model/palette';
import {brandColor} from 'shared/lib/colors';

import styles from './MultiSelectWithCheckbox.module.scss';

export interface OptionNode {
    id: string;
    value: string;
    label: string;
    children?: OptionNode[];
    hidden?: boolean;
    // Невыбираемый заголовок группы: чекбокса нет, выбираются только дочерние опции.
    isGroup?: boolean;
}

export interface MultiSelectHandle {
    openDropdown: () => void;
}

interface IMultiSelectWithCheckboxProps {
    options: OptionNode[];
    value: string[];
    /* eslint-disable no-unused-vars */
    onChange: (selected: string[]) => void;
    placeholder?: string;
    name?: string;
    fieldState?: any;
    hideIcon?: boolean;
    maxCount?: number;
    isFreeTariff?: boolean;
    disabledTooltip?: string;
    onClose?: () => void;
    searchable?: boolean;
    searchPlaceholder?: string;
    onSearch?: (query: string) => void;
}

const MultiSelectWithCheckbox = forwardRef<MultiSelectHandle, IMultiSelectWithCheckboxProps>(({
    options,
    value,
    onChange,
    placeholder = t`Выберите...`,
    name,
    fieldState,
    hideIcon,
    maxCount,
    isFreeTariff,
    disabledTooltip = t`Выбор локаций доступен на платных тарифах`,
    onClose,
    searchable = false,
    searchPlaceholder = t`Поиск`,
    onSearch,
}, ref) => {
    const [open, setOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [touched, setTouched] = useState(false);
    const [query, setQuery] = useState('');

    const containerRef = useRef<HTMLDivElement>(null);
    const prevOpenRef = useRef(open);
    const onCloseRef = useRef(onClose);
    const onSearchRef = useRef(onSearch);

    useEffect(() => {
        onCloseRef.current = onClose;
        onSearchRef.current = onSearch;
    }, [onClose, onSearch]);

    useEffect(() => {
        if (onSearchRef.current) {
            onSearchRef.current(query);
        }
    }, [query]);

    useImperativeHandle(ref, () => ({
        openDropdown: () => {
            setOpen(true);
            setIsFocused(true);
        },
    }));

    useEffect(() => {
        if (!open && isFocused) {

            setTouched(true);
            setIsFocused(false);
        }
    }, [open, isFocused]);

    useEffect(() => {
        if (prevOpenRef.current && !open) {
            onCloseRef.current?.();
            setQuery('');
        }
        prevOpenRef.current = open;
    }, [open]);

    let state: 'filled' | 'error' | 'valid' | 'default';

    if (open) {
        state = 'filled';
    } else if (touched && (!value || value.length === 0)) {
        state = 'error';
    } else {
        state = getInputState({
            fieldState,
            value: value && value.length > 0,
            validate: isFocused,
        });
    }

    const iconsMap = {
        valid: <Correct color={brandColor(PALETTE.accent, PALETTE.success)}/>,
        filled: <Edit />,
        default: <Edit />,
        error: <Wrong />,
    };

    const classMap = isTranslate()
        ? {
            valid: styles.enSelect_valid,
            filled: styles.enSelect_active,
            default: styles.enSelect_default,
            error: styles.enSelect_error,
        }
        : {
            valid: styles.select_valid,
            filled: styles.select_active,
            default: styles.select_default,
            error: styles.select_error,
        };

    const baseClass = isTranslate() ? styles.enSelect : styles.select;

    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setIsFocused(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const limitReached = typeof maxCount === 'number' && maxCount > 0 && value.length >= maxCount;

    const normalizedQuery = onSearch ? '' : query.trim().toLowerCase();
    const matchesQuery = (label: string) => label.toLowerCase().includes(normalizedQuery);
    const getVisibleChildren = (parent: OptionNode) => {
        if (!parent.children) return [];
        if (!normalizedQuery || matchesQuery(parent.label)) return parent.children;
        return parent.children.filter((ch) => matchesQuery(ch.label));
    };
    const isOptionVisible = (opt: OptionNode) => {
        if (!normalizedQuery) return true;
        if (matchesQuery(opt.label)) return true;
        return getVisibleChildren(opt).length > 0;
    };
    const visibleOptions = options.filter((opt) => !opt.hidden && isOptionVisible(opt));

    const isParentIndeterminate = (parent: OptionNode) => {
        if (!parent.children) return false;
        const allChildren = parent.children.map((ch) => ch.id);
        const checkedCount = allChildren.filter((v) => value.includes(v)).length;
        return checkedCount > 0 && checkedCount < allChildren.length && !value.includes(parent.id);
    };

    const handleParentChange = (parent: OptionNode, checked: boolean) => {
        if (!parent.children) {
            if (checked) onChange([...value, parent.id]);
            else onChange(value.filter((v) => v !== parent.id));
            return;
        }
        const childrenIds = parent.children.map((ch) => ch.id);
        if (checked) {
            onChange([
                ...value.filter((v) => !childrenIds.includes(v)),
                parent.id,
                ...childrenIds,
            ]);
        } else {
            onChange(value.filter((v) => v !== parent.id && !childrenIds.includes(v)));
        }
    };

    const handleChildChange = (parent: OptionNode, child: OptionNode, checked: boolean) => {
        if (!parent.children) return;
        const childrenIds = parent.children.map((ch) => ch.id);
        let newValue = checked
            ? [...value.filter((v) => v !== parent.id), child.id]
            : value.filter((v) => v !== child.id && v !== parent.id);

        const checkedChildren = childrenIds.filter((v) => newValue.includes(v));
        if (checkedChildren.length === childrenIds.length) {
            newValue = [...newValue.filter((v) => !childrenIds.includes(v)), parent.id];
        }
        onChange(newValue);
    };

    const handleLeafChange = (id: string, checked: boolean) => {
        if (checked) onChange([...value, id]);
        else onChange(value.filter((v) => v !== id));
    };

    const handleRemove = (removedId: string) => {
        let newValue = value.filter(v => v !== removedId);
        const parent = options.find(opt => opt.id === removedId);
        if (parent && parent.children) {
            const childrenIds = parent.children.map(ch => ch.id);
            newValue = newValue.filter(v => !childrenIds.includes(v));
        }
        options.forEach(opt => {
            if (opt.children && opt.children.some(ch => ch.id === removedId)) {
                if (newValue.includes(opt.id)) {
                    newValue = newValue.filter(v => v !== opt.id);
                }
            }
        });
        onChange(newValue);
    };

    const getParentShortLabel = (parentLabel: string) => {
        if (parentLabel === 'Подтвержденные проблемные') return 'подтв.';
        if (parentLabel === 'Неподтвержденные проблемные') return 'неподтв.';
        return; 
    };

    const getSelectedChips = () => {
        const chips: { id: string; label: string }[] = [];
        options.forEach(opt => {
            if (opt.children) {
                if (value.includes(opt.id)) {
                    // Если выбран родитель — показываем только его
                    chips.push({ id: opt.id, label: opt.label });
                } else {
                    // Если родитель не выбран — показываем отмеченных детей с припиской
                    const parentShort = getParentShortLabel(opt.label);
                    opt.children.forEach(ch => {
                        if (value.includes(ch.id)) {
                            chips.push({
                                id: ch.id,
                                label: parentShort
                                    ? `${ch.label} (${parentShort})`
                                    : ch.label,
                            });
                        }
                    });
                }
            } else {
                if (value.includes(opt.id)) {
                    chips.push({ id: opt.id, label: opt.label });
                }
            }
        });
        return chips;
    };

    return (
        <div
            className={cn(
                styles.select_container,
                { [styles.select_open]: open },
                { [styles.select_no_icon_select]: hideIcon },
            )}
            ref={containerRef}
        >
            <select
                multiple
                name={name}
                value={value}
                onChange={() => undefined}
                className={styles.hiddenSelect}
                tabIndex={-1}
            >
                {options.flatMap(opt => {
                    if (opt.children) {
                        const childOptions = opt.children.map(
                            child => <option key={child.id} value={child.id}>{child.label}</option>);
                        // У заголовка группы нет собственного значения — только дочерние опции.
                        return opt.isGroup
                            ? childOptions
                            : [<option key={opt.id} value={opt.id}>{opt.label}</option>, ...childOptions];
                    }
                    return <option key={opt.id} value={opt.id}>{opt.label}</option>;
                })}
            </select>
            {!hideIcon &&
                <div className={styles.icon}>
                    {iconsMap[state]}
                </div>}
            <div
                className={cn(
                    baseClass,
                    classMap[state],
                    { [styles.select_open]: open },
                )}
                tabIndex={0}
                onClick={() => { setOpen((o) => !o); setIsFocused(true); }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOpen((o) => !o)}
            >
                {getSelectedChips().length === 0 ? (
                    <span className={styles.placeholder}>{placeholder}</span>
                ) : (
                    <div className={styles.chips}>
                        {getSelectedChips().map(({ id, label }) => (
                            <span
                                className={cn(
                                    styles.chip,
                                    isFreeTariff && id === 'auto-select' && styles.freeTariffChip,
                                )}
                                key={id}
                                title={id === 'auto-select'
                                    ? t`Наиболее свободная локация на выбор Overseer`
                                    : undefined
                                }
                            >
                                {label}
                                <span
                                    className={styles.chipRemove}
                                    onClick={e => {
                                        e.stopPropagation();
                                        handleRemove(id);
                                    }}
                                >
                                    <Close />
                                </span>
                            </span>
                        ))}
                    </div>
                )}
                <span className={styles.arrow}>
                    <ArrowDown/>
                </span>
            </div>
            {open && (
                <div className={styles.dropdown}>
                    {searchable && (
                        <div className={styles.searchBox} onClick={(e) => e.stopPropagation()}>
                            <input
                                className={styles.searchInput}
                                type="text"
                                value={query}
                                placeholder={searchPlaceholder}
                                onChange={(e) => setQuery(e.target.value)}
                                autoFocus
                            />
                        </div>
                    )}
                    {searchable && visibleOptions.length === 0 && (
                        <div className={styles.emptyResult}>{t`Ничего не найдено`}</div>
                    )}
                    {visibleOptions.map((parent) => (
                        <div key={parent.id} className={styles.parentBlock}>
                            {parent.isGroup ? (
                                <div className={styles.groupHeader}>{parent.label}</div>
                            ) : (
                                <div className={cn(
                                    styles.parentContent,
                                    styles[isTranslate() ? 'enColor' : ''],
                                )}>
                                    <div title={isFreeTariff ? disabledTooltip : undefined}>
                                        <Checkbox
                                            className={styles.checkbox}
                                            checked={value.includes(parent.id)}
                                            indeterminate={isParentIndeterminate(parent)}
                                            disabled={limitReached && !value.includes(parent.id)
                                                && !(parent.children
                                                    && parent.children.some(ch => value.includes(ch.id)))
                                                || isFreeTariff}
                                            onChange={e => handleParentChange(parent, e.target.checked)}
                                        >
                                            <span className={styles.parentLabel}>{parent.label}</span>
                                        </Checkbox>
                                    </div>
                                </div>
                            )}
                            {parent.children && (
                                <div className={styles.childrenBlock}>
                                    {getVisibleChildren(parent).map((child) => (
                                        <div key={child.id} className={styles.childContent}>
                                            <div title={isFreeTariff ? disabledTooltip : undefined}>
                                                <Checkbox
                                                    className={styles.checkbox}
                                                    checked={value.includes(child.id)}
                                                    disabled={limitReached && !value.includes(child.id) || isFreeTariff}
                                                    onChange={e => parent.isGroup
                                                        ? handleLeafChange(child.id, e.target.checked)
                                                        : handleChildChange(parent, child, e.target.checked)}
                                                >
                                                    <span className={styles.childLabel}>{child.label}</span>
                                                </Checkbox>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

MultiSelectWithCheckbox.displayName = 'MultiSelectWithCheckbox';

export default MultiSelectWithCheckbox;